import { concat, hash } from '@ember/helper';
import { currencyStringToNumber, generateRandomString } from '../../utils/format';
import { fn } from '@ember/helper';
import { getAllValuesForTasField } from '../../utils/tuition-assistance/fields';
import { Input } from 'tio-ui/components/forms';
import { Button } from 'tio-ui/components/buttons';
import { modifier } from 'ember-modifier';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { Drawer } from 'tio-ui/components/overlays';
import inputmask from '../../modifiers/inputmask';
import { lowercase } from 'tio-ui/utilities';
import type { ReceivedScholarship } from '../../types/tuition-assistance';
import type { TASCourseModelFieldsSignature } from '../../models/tas-course';
import type TasApplication from '../../models/tas-application';
import { PhX } from 'ember-phosphor-icons';

interface AddScholarshipsDrawerSignature {
  Args: {
    isOpen: boolean;
    application: TasApplication;
    onClose: () => void;
    scholarshipIdToEdit?: string;
  };
  Element: HTMLDivElement;
}

export type Scholarship = {
  id?: string;
  scholarshipAmount: number;
  scholarshipName: string;
  error?: string;
};

export default class AddScholarshipsDrawerComponent extends Component<AddScholarshipsDrawerSignature> {
  @tracked scholarships: Scholarship[] = [];
  inputClasses = { base: 'w-full mb-6 p-1' };

  // Modifier to load scholarships when drawer opens
  loadOnOpen = modifier(() => {
    if (this.args.isOpen) {
      this.loadExistingScholarships();
    }
  });

  // Modifier to focus the scholarship amount input if editing a specific scholarship
  focusIfEditing = modifier((element: HTMLElement, [scholarshipId]: [string | undefined]) => {
    if (this.args.scholarshipIdToEdit && scholarshipId === this.args.scholarshipIdToEdit) {
      // Use requestAnimationFrame to wait for the drawer to be painted
      // Then wait for any CSS transitions to complete
      let handled = false;

      const focusInput = () => {
        if (handled) return;
        handled = true;

        const input = element.querySelector('input');
        if (input) {
          // Scroll the element into view first
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Focus and select the input
          input.focus();
          input.select();
        }
      };

      // Listen for transitionend on the drawer container
      const drawerContainer = element.closest('[role="dialog"]');
      const onTransitionEnd = (event: Event) => {
        // Check if this is a transform or opacity transition (typical drawer animations)
        const transitionEvent = event as TransitionEvent;
        if (
          transitionEvent.propertyName === 'transform' ||
          transitionEvent.propertyName === 'opacity'
        ) {
          focusInput();
          drawerContainer?.removeEventListener('transitionend', onTransitionEnd);
        }
      };

      if (drawerContainer) {
        drawerContainer.addEventListener('transitionend', onTransitionEnd);
      }

      // Fallback: If no transition fires within 250ms, focus anyway
      const fallbackTimer = setTimeout(() => {
        focusInput();
        drawerContainer?.removeEventListener('transitionend', onTransitionEnd);
      }, 250);

      return () => {
        clearTimeout(fallbackTimer);
        drawerContainer?.removeEventListener('transitionend', onTransitionEnd);
      };
    }
  });

  loadExistingScholarships() {
    const existingScholarships = getAllValuesForTasField(
      'SCHOLARSHIPS_RECEIVED' as keyof TASCourseModelFieldsSignature,
      this.args.application.fields as unknown as TASCourseModelFieldsSignature
    ) as ReceivedScholarship[];

    this.scholarships = existingScholarships
      .filter((scholarship): scholarship is ReceivedScholarship => scholarship !== null)
      .map((scholarship, index) => ({
        scholarshipName: scholarship.scholarshipName,
        scholarshipAmount: scholarship.scholarshipAmount,
        id: scholarship.scholarshipId || `scholarship-${index}`,
        error: '',
      }));
  }

  getErrorForInput(id: string = '', scholarships: Scholarship[]) {
    return scholarships.find((scholarship: Scholarship) => scholarship.id === id)?.error ?? '';
  }

  formatAmount(amount: number) {
    // Amount is stored in cents, convert to dollars for display
    return (amount / 100).toString();
  }

  get programTemplate() {
    return this.args.application.tasProgramInstance.tasProgramTemplate;
  }

  get scholarshipName() {
    return this.programTemplate.fields['SCHOLARSHIP_NAME'];
  }

  get scholarshipAmount() {
    return this.programTemplate.fields['SCHOLARSHIP_AMOUNT'];
  }

  get scholarshipReceived() {
    return this.programTemplate.fields['SCHOLARSHIPS_RECEIVED'];
  }

  addScholarship = () => {
    this.scholarships.push({
      scholarshipName: '',
      scholarshipAmount: 0,
      error: '',
      id: generateRandomString(),
    });
    this.scholarships = [...this.scholarships];
  };

  removeScholarship = (id: string = '') => {
    this.scholarships = this.scholarships.filter((scholarship) => scholarship.id !== id);
  };

  closeDrawer = () => {
    this.args.onClose();
  };

  setName = (index: number, value: string) => {
    this.scholarships[index]!.scholarshipName = value;
  };

  setAmount = (index: number, value: string) => {
    this.scholarships[index]!.scholarshipAmount = currencyStringToNumber(value);
  };

  checkForMissingFields = async () => {
    this.scholarships.forEach((scholarship) => {
      if (
        (scholarship.scholarshipName === '' && this.scholarshipName.required) ||
        (scholarship.scholarshipAmount === 0 && this.scholarshipAmount.required)
      ) {
        scholarship.error = 'A required field is missing';
      } else {
        scholarship.error = '';
      }
      this.scholarships = [...this.scholarships];
    });
  };

  saveScholarships = async () => {
    await this.checkForMissingFields();
    const hasErrors = this.scholarships.some((scholarship) => scholarship.error !== '');
    if (hasErrors) {
      return;
    }
    //save scholarships - directly update application fields
    this.args.application.fields['SCHOLARSHIPS_RECEIVED'] = {
      values: this.scholarships.map((scholarship) => ({
        scholarshipName: scholarship.scholarshipName,
        scholarshipAmount: scholarship.scholarshipAmount,
        scholarshipId: scholarship.id,
      })) as ReceivedScholarship[],
    };
    await this.args.application.save();
    // Call onClose to notify parent component to reload scholarship data
    this.args.onClose();
  };

  <template>
    <Drawer
      @isOpen={{@isOpen}}
      @onClose={{@onClose}}
      @allowCloseButton={{false}}
      @size="lg"
      @placement="right"
      @backdrop="faded"
      @closeOnOutsideClick={{false}}
      @closeOnEscapeKey={{false}}
      @classes={{hash base="flex flex-col h-screen max-h-screen overflow-hidden"}}
      {{this.loadOnOpen}}
      as |d|
    >
      <d.Header class="bg-gray-50 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <h2 class="text-gray-600 font-semibold">
              {{t
                "common.tas.add_scholarships_drawer.add_scholarships"
                label=this.scholarshipReceived.label
              }}
            </h2>
            <p class="text-gray-400 text-sm">
              {{t
                "common.tas.add_scholarships_drawer.fill_out_scholarship_below"
                label=(lowercase this.scholarshipReceived.label)
              }}
            </p>
          </div>
        </div>
      </d.Header>

      <d.Body class="px-6 flex flex-col min-h-0 overflow-hidden">
        <div class="overflow-y-auto flex-1">
          <div class="flex py-4">
            <div class="my-4">
              <h1 class="text-gray-600 text-xl">
                {{t
                  "common.tas.add_scholarships_drawer.scholarship_information"
                  label=this.scholarshipReceived.label
                }}
              </h1>
              <p class="text-sm">
                <span class="text-error-400">*</span>
                {{t "common.tas.add_scholarships_drawer.required_fields"}}
              </p>
            </div>
          </div>
          {{#if this.scholarships.length}}
            {{#each this.scholarships as |scholarship index|}}
              <div class="flex w-full items-center">
                <div class="flex flex-col w-full">
                  <label for="scholarship_name4" class="font-semibold text-gray-600">
                    {{this.scholarshipName.label}}
                    {{#if this.scholarshipName.required}}
                      <span class="text-error-400">*</span>
                    {{/if}}
                  </label>
                  <Input
                    data-test={{concat "scholarship-name-" index}}
                    @classes={{this.inputClasses}}
                    @value={{scholarship.scholarshipName}}
                    @onChange={{fn this.setName index}}
                    @isRequired={{true}}
                  />
                  <label for="scholarship_amount4" class="font-semibold text-gray-600">
                    {{this.scholarshipAmount.label}}
                    {{#if this.scholarshipAmount.required}}
                      <span class="text-error-400">*</span>
                    {{/if}}
                  </label>
                  <div {{this.focusIfEditing scholarship.id}}>
                    <Input
                      data-test={{concat "scholarship-amount-" index}}
                      @classes={{this.inputClasses}}
                      @value={{this.formatAmount scholarship.scholarshipAmount}}
                      @onChange={{fn this.setAmount index}}
                      @isRequired={{true}}
                      {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
                    />
                  </div>
                  <p class="text-danger">{{this.getErrorForInput
                      scholarship.id
                      this.scholarships
                    }}</p>
                </div>
                <Button
                  @appearance="minimal"
                  @intent="danger"
                  class="ml-4"
                  {{on "click" (fn this.removeScholarship scholarship.id)}}
                  data-test-remove-scholarship
                >
                  <PhX @size="20" />
                </Button>
              </div>
              <hr class="h-px my-8 bg-gray-200 border-0 border-gray-200 dark:bg-gray-700" />
            {{/each}}
            <div class="flex py-4">
              <Button
                @intent="primary"
                data-test-add-scholarship
                {{on "click" this.addScholarship}}
              >
                {{t
                  "common.tas.add_scholarships_drawer.add_another_scholarship"
                  label=this.scholarshipReceived.label
                }}
              </Button>
            </div>
          {{else}}
            <div class="py-8">
              <h3 class="text-xl font-semibold leading-6">
                {{t
                  "common.tas.add_scholarships_drawer.no_scholarships_added"
                  label=this.scholarshipReceived.label
                }}
              </h3>
              <div class="mt-2 flex-col gap-2 sm:flex sm:items-start sm:justify-between">
                <div class="max-w-xl text-sm text-gray-500">
                  <p>{{t
                      "common.tas.add_scholarships_drawer.no_scholarships_added_info"
                      label=(lowercase this.scholarshipReceived.label)
                    }}</p>
                </div>
                <div class="m-5 sm:ml-6 sm:mt-0 sm:flex sm:shrink-0 sm:items-center">
                  <Button
                    @intent="primary"
                    data-test-add-scholarship
                    {{on "click" this.addScholarship}}
                  >
                    {{t
                      "common.tas.add_scholarships_drawer.add_scholarship"
                      label=this.scholarshipReceived.label
                    }}
                  </Button>
                </div>
              </div>
            </div>
          {{/if}}
        </div>
      </d.Body>

      <d.Footer class="border-t border-gray-200 bg-white w-full">
        <div class="flex justify-start items-center gap-4 w-full">
          <Button
            @appearance="minimal"
            {{on "click" this.closeDrawer}}
            data-test-close-scholarship-drawer
          >
            {{t "common.cancel"}}
          </Button>
          <Button
            @intent="primary"
            {{on "click" this.saveScholarships}}
            data-test-save-scholarships
          >
            {{t "common.save"}}
          </Button>
        </div>
      </d.Footer>
    </Drawer>
  </template>
}
